import { WidenPrimitives } from '@growthbook/growthbook'
import { getCookie, setCookie } from 'cookies-next'
import { IncomingMessage, ServerResponse } from 'http'
import { v4 } from 'uuid'
import { GROWTHBOOK_IS_ANGEL_EMPLOYEE_COOKIE, SEGMENT_ANONYMOUS_ID_COOKIE } from '@/constants/cookies'
import { Durations } from '@/constants/durations'
import { AppFeatures } from '@/experimentation/app-features'
import { getExperimentValueFromQuery, getGrowthbook, shouldStringifyValue } from '@/experimentation/utils'
import { extractAnonymousIdFromRequest } from '@/services/requests'
import { track } from '@/utils/SegmentNode'
import { getCookieOptions } from '@/utils/cookies/auth-cookie-utils'
import { logger } from '@/utils/logging'
import { getUserUuidFromJwt } from '@/utils/users/usersServer'
import { DeferredTrackingCalls, ServerExperiments } from './types'

type RequestWithCookies = IncomingMessage & { cookies: Partial<{ [p: string]: string }> }

interface GrowthBookInitOptions {
  /** When true, tracking fires immediately. When false, calls are deferred for later firing. */
  enableTracking: boolean
}

/**
 * Core initialization logic for GrowthBook on the server.
 * Shared by both immediate-tracking and deferred-tracking modes.
 */
const initializeGrowthbookCore = async (
  req: RequestWithCookies,
  res: ServerResponse,
  options: GrowthBookInitOptions,
) => {
  const anonymousIdFromRequest = extractAnonymousIdFromRequest(req)
  const fallbackAnonymousId = v4()
  const anonymousId = anonymousIdFromRequest ?? fallbackAnonymousId

  // Reset cookie if it doesn't exist. This should never happen.
  if (!anonymousIdFromRequest) {
    logger().error('No anonymous ID found in request - growthbook will use fallback', { req })

    setCookie(
      SEGMENT_ANONYMOUS_ID_COOKIE,
      anonymousId,
      getCookieOptions({ req, res, maxAge: Durations.TEN_YEARS_IN_SECONDS, crossSubdomain: true }),
    )
  }

  const growthbook = getGrowthbook(
    {
      subscribeToChanges: false,
      backgroundSync: false,
      // When tracking is disabled, GrowthBook stores calls for later retrieval via getDeferredTrackingCalls()
      ...(options.enableTracking ? {} : { trackingCallback: undefined }),
    },
    options.enableTracking
      ? async (eventName, payload) => await track(eventName, { anonymousId, ...payload })
      : async () => {}, // No-op when tracking is deferred
  )

  // Give GB two attempts to initialize
  const gbRes = await growthbook.init()
  if (gbRes.error) await growthbook.init()

  const userId = getUserUuidFromJwt(req, res) ?? null
  const isAngelEmployeeCookie = getCookie(GROWTHBOOK_IS_ANGEL_EMPLOYEE_COOKIE, { req, res })
  const isAngelEmployeeUser = isAngelEmployeeCookie === true || isAngelEmployeeCookie === 'true'

  await growthbook.setAttributes({ id: anonymousId, userId, isAngelEmployeeUser })
  return growthbook
}

export const initializeGrowthbookOnServer = async (req: RequestWithCookies, res: ServerResponse) => {
  return initializeGrowthbookCore(req, res, { enableTracking: true })
}

/**
 * Initialize GrowthBook WITHOUT tracking callback.
 * Used for deferred tracking - get variant values without firing impressions.
 */
const initializeGrowthbookOnServerNoTracking = async (req: RequestWithCookies, res: ServerResponse) => {
  return initializeGrowthbookCore(req, res, { enableTracking: false })
}

export const setupServerExperiments = async (
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> },
  res: ServerResponse,
) => {
  const growthbook = await initializeGrowthbookOnServer(req, res)
  return {
    getFeatureValue: growthbook.getFeatureValue.bind(growthbook),
    runInlineExperiment: growthbook.run.bind(growthbook),
    destroy: growthbook.destroy.bind(growthbook),
    anonymousId: growthbook.getAttributes().id,
  }
}

/**
 * @deprecated Use the `runExperimentsOnServer` function instead. It has better performance.
 */
export const runExperimentOnServer = async <T extends keyof AppFeatures>(
  experimentKey: T,
  defaultValue: AppFeatures[T],
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> },
  res: ServerResponse,
): Promise<WidenPrimitives<AppFeatures[T]>> => {
  const fromQuery = getSearchParam(req, experimentKey)
  if (fromQuery !== null) {
    return getExperimentValueFromQuery(fromQuery) as WidenPrimitives<AppFeatures[T]>
  }

  const { getFeatureValue, destroy } = await setupServerExperiments(req, res)
  const variation = getFeatureValue(experimentKey, defaultValue)

  destroy()
  return variation
}

interface ExperimentParams<T extends keyof AppFeatures> {
  experimentKey: T
  defaultValue: AppFeatures[T]
  skip?: boolean
}

type ExperimentParamsUnion = {
  [K in keyof AppFeatures]: ExperimentParams<K>
}[keyof AppFeatures]

export type ExperimentParamsArray = ExperimentParamsUnion[]

export async function runExperimentsOnServer(
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> },
  res: ServerResponse,
  experiments: ExperimentParamsArray,
): Promise<ServerExperiments> {
  const { getFeatureValue, destroy } = await setupServerExperiments(req, res)

  const result = experiments.reduce((prev, { experimentKey, defaultValue, skip }) => {
    if (skip) return prev
    const fromQuery = getSearchParam(req, experimentKey)
    const v = fromQuery !== null ? getExperimentValueFromQuery(fromQuery) : getFeatureValue(experimentKey, defaultValue)
    prev[experimentKey] = v
    return prev
  }, {} as ServerExperiments)

  destroy()
  return result
}

/**
 * Result type for experiments with deferred tracking.
 * Contains both experiment values and deferred tracking calls to fire on the client.
 */
export interface DeferredExperimentResult {
  experiments: ServerExperiments
  deferredTrackingCalls: DeferredTrackingCalls
}

/**
 * Run experiments on server with mixed immediate and deferred tracking.
 *
 * - Immediate experiments: Tracking fires right away on the server
 * - Deferred experiments: Tracking is captured but not fired; client must fire when user sees variation
 *
 * Use this when some experiments should only be tracked when the user actually sees the variation
 * (e.g., a card checkout form that may never be shown if user chooses PayPal).
 *
 * This uses a single GrowthBook instance without a tracking callback, then manually fires
 * tracking for immediate experiments. This is more efficient than creating two instances.
 */
export async function runExperimentsOnServerWithDeferred(
  req: RequestWithCookies,
  res: ServerResponse,
  immediateExperiments: ExperimentParamsArray,
  deferredExperiments: ExperimentParamsArray,
): Promise<DeferredExperimentResult> {
  // Use a single GrowthBook instance without tracking callback
  // We'll manually fire tracking for immediate experiments
  const growthbook = await initializeGrowthbookOnServerNoTracking(req, res)
  const attributes = growthbook.getAttributes()
  const anonymousId = typeof attributes.id === 'string' ? attributes.id : ''
  if (!anonymousId) {
    logger().error('GrowthBook anonymousId is missing or invalid', { attributes })
  }

  const allExperiments = [...immediateExperiments, ...deferredExperiments]
  const immediateKeys = new Set<string>(immediateExperiments.map((e) => e.experimentKey))

  // Get all experiment values
  const result = allExperiments.reduce((prev, { experimentKey, defaultValue, skip }) => {
    if (skip) return prev
    const fromQuery = getSearchParam(req, experimentKey)
    const v =
      fromQuery !== null
        ? getExperimentValueFromQuery(fromQuery)
        : growthbook.getFeatureValue(experimentKey, defaultValue)
    prev[experimentKey] = v
    return prev
  }, {} as ServerExperiments)

  // Get all deferred tracking calls from GrowthBook
  // GrowthBook stores these when no trackingCallback is provided
  const allDeferredCalls: DeferredTrackingCalls = growthbook.getDeferredTrackingCalls()

  // Separate calls: fire immediate ones now, keep deferred ones for client
  const deferredTrackingCalls: DeferredTrackingCalls = []

  for (const call of allDeferredCalls) {
    if (immediateKeys.has(call.experiment.key)) {
      // Fire immediately for "immediate" experiments
      // Use shouldStringifyValue for consistency with normal trackingCallback behavior
      const resultValue = shouldStringifyValue(call.result.value)
        ? JSON.stringify(call.result.value)
        : call.result.value
      await track('Experiment Impression', {
        anonymousId,
        name: call.experiment.key,
        variation: resultValue,
      })
    } else {
      // Keep for client-side firing
      deferredTrackingCalls.push(call)
    }
  }

  growthbook.destroy()

  return {
    experiments: result,
    deferredTrackingCalls,
  }
}

export async function runInlineExperimentOnServer(
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> },
  res: ServerResponse,
  experimentKey: string,
  variations: [string, string, ...string[]],
): Promise<string | number | boolean> {
  const { runInlineExperiment, destroy } = await setupServerExperiments(req, res)

  const fromQuery = getSearchParam(req, experimentKey)
  const v =
    fromQuery !== null
      ? getExperimentValueFromQuery(fromQuery)
      : runInlineExperiment({
          key: experimentKey,
          variations,
        }).value

  destroy()
  return v
}

type Variation = { sys: { id: string }; __typename?: string; rolloutVariationId?: string | null } | undefined | null

type VariationReturn<T> = NonNullable<
  Exclude<T, { __typename: 'GrowthbookExperimentMappings' } | { __typename?: 'GrowthbookExperimentMappings' }>
>

const isValidVariation = <T extends Variation>(v: T): v is VariationReturn<T> => {
  return (
    !!v &&
    'sys' in v &&
    typeof v.sys === 'object' &&
    !!v.sys &&
    'id' in v.sys &&
    !!v?.sys?.id &&
    v.__typename !== 'GrowthbookExperimentMappings'
  )
}

const getValidVariations = <T extends Variation>(variations: T[]): VariationReturn<T>[] => {
  return variations.filter(isValidVariation)
}

export const isValidVariationArray = (arr: string[]): arr is [string, string, ...string[]] => {
  return arr.length >= 2
}

export async function runContentfulExperimentOnServer<T extends Variation>(
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> },
  res: ServerResponse,
  rawVariations: T[],
): Promise<VariationReturn<T> | null> {
  try {
    const experimentMapping = rawVariations.find((v) => v?.__typename === 'GrowthbookExperimentMappings')
    const variations = getValidVariations(rawVariations)

    const firstVariation = variations[0]
    const rolledOutVariation = variations.find((v) => v.sys.id === experimentMapping?.rolloutVariationId)

    if (variations.length === 1 && firstVariation) return firstVariation
    if (rolledOutVariation) return rolledOutVariation

    if (!experimentMapping?.sys?.id || variations.length === 0 || !firstVariation) return null

    const variationIds = variations.map((v) => v.sys.id)

    if (!isValidVariationArray(variationIds)) return firstVariation

    const experimentValue = await runInlineExperimentOnServer(req, res, experimentMapping.sys.id, variationIds)

    return variations.find((v) => v.sys.id === experimentValue) || firstVariation
  } catch (err) {
    logger().error(
      'Error running contentful experiments on server',
      {
        experimentMapping: rawVariations.find((v) => v?.__typename === 'GrowthbookExperimentMappings') ?? null,
      },
      err,
    )
    return null
  }
}

function getSearchParam(req: IncomingMessage, key: string): string | null {
  if (!req.url) return null
  const protocol = req.headers['x-forwarded-proto'] === 'https' ? 'https' : 'http'
  const url = new URL(req.url, `${protocol}://${req.headers.host}`)
  return url.searchParams.get(key)
}
